/**
 * Thematische Lagebeschreibungs-Boxen (Makro- + Mikro-Grid) mit Section-/Sub-Ratings.
 * Aus DescriptionDisplay extrahiert — wird jetzt in der Übersicht gezeigt.
 */

import type { LageSection, LagepromptResponse } from '../../types/api'

/**
 * Icon-Zuordnung über Stichwörter im Bereichs-Key statt über feste Keys:
 * Die Keys stammen aus dem im Admin editierbaren Layout und können sich ändern —
 * eine 1:1-Map würde bei jedem neuen/umbenannten Bereich ins Leere laufen.
 * Erste passende Regel gewinnt, sonst greift DEFAULT_ICON.
 */
const DEFAULT_ICON = '📄'
const SECTION_ICON_RULES: Array<[RegExp, string]> = [
  [/fazit|zusammenfassung/, '📋'],
  [/markt|preis|miete|bauland/, '🏦'],
  [/demograph|bevoelkerung|passung/, '👥'],
  [/wirtschaft|arbeit|prognos|zukunft/, '📈'],
  [/infrastruktur|erreichbar|verkehr|anbindung/, '🚗'],
  [/nahversorg|versorgung/, '🛒'],
  [/dynamik/, '🔄'],
  [/geograph|einordnung|einleitung|gemeindedaten|stammdaten/, '📍'],
]

function sectionIcon(key: string): string {
  const k = key.toLowerCase()
  for (const [pattern, icon] of SECTION_ICON_RULES) {
    if (pattern.test(k)) return icon
  }
  return DEFAULT_ICON
}

function scoreColor(score: number): string {
  if (score >= 70) return 'bg-green-500'
  if (score >= 50) return 'bg-lime-500'
  if (score >= 40) return 'bg-yellow-500'
  if (score >= 25) return 'bg-orange-500'
  return 'bg-red-500'
}

/**
 * Rating-Zeile unten in der Box: Begriff + Score + Mini-Balken + Sub-Chips.
 * Haupt-Rating und Sub-Ratings sind unabhängig: Bereiche ohne eigenen `rating_key`
 * (Mikrolage) tragen ihre Wertungen ausschließlich in `sub_ratings`.
 */
function SectionRating({ section }: { section: LageSection }) {
  const subs = section.sub_ratings ?? []
  if (!section.rating && subs.length === 0) return null
  return (
    <div className="mt-3 pt-3 border-t border-gray-100">
      {section.rating && (
        <>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Rating</span>
            <span className="text-sm">
              <span className="font-semibold text-gut8-anthracite">„{section.rating.term}"</span>
              <span className="ml-2 font-mono text-xs text-gray-500">{section.rating.score.toFixed(1)}</span>
            </span>
          </div>
          <div className="w-full rounded-full bg-gray-100 h-2">
            <div
              className={`${scoreColor(section.rating.score)} h-2 rounded-full`}
              style={{ width: `${Math.max(2, Math.min(100, section.rating.score))}%` }}
            />
          </div>
        </>
      )}
      {subs.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {subs.map((sub) => (
            <span
              key={sub.key}
              title={`Score: ${sub.score.toFixed(1)}`}
              className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-[11px] text-gray-600"
            >
              {sub.label}: „{sub.term}"
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

function SectionBox({ section, wide }: { section: LageSection; wide?: boolean }) {
  const isFazit = section.key === 'fazit'
  return (
    <div
      className={`card-gut8 ${wide ? 'lg:col-span-2' : ''} ${
        isFazit ? 'border-2 border-secondary/40 bg-secondary-subtle/30' : ''
      }`}
    >
      <h4 className="font-semibold text-gut8-anthracite mb-2 flex items-center">
        <span className="text-secondary mr-2">{sectionIcon(section.key)}</span>
        {section.title}
      </h4>
      <p className="text-sm text-gut8-anthracite leading-relaxed">{section.text}</p>
      <SectionRating section={section} />
    </div>
  )
}

function SectionGrid({ heading, sections }: { heading: string; sections: LageSection[] }) {
  // Bereiche ohne Text (z. B. leeres Template) erzeugen keine leere Box.
  const visible = sections.filter((s) => (s.text ?? '').trim().length > 0)
  const fazit = visible.find((s) => s.key === 'fazit')
  const rest = visible.filter((s) => s.key !== 'fazit')
  // Der erste Bereich einer Ebene ist die Einordnung und läuft über die volle Breite.
  const intro = rest[0]
  const themes = rest.slice(1)
  if (visible.length === 0) return null
  return (
    <div>
      <h3 className="text-xl font-semibold text-gut8-anthracite mb-4">{heading}</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {intro && <SectionBox section={intro} wide />}
        {themes.map((s) => (
          <SectionBox key={s.key} section={s} />
        ))}
        {fazit && <SectionBox section={fazit} wide />}
      </div>
    </div>
  )
}

/** Makro- + Mikro-Grid der Lagebeschreibung. */
export function LageSectionBoxes({ data }: { data: LagepromptResponse }) {
  const desc = data.description
  return (
    <div className="space-y-6">
      <SectionGrid heading={desc.makrolage_heading} sections={desc.makro_sections} />
      <SectionGrid heading={desc.mikrolage_heading} sections={desc.mikro_sections} />
    </div>
  )
}
